import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate, useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_DATA_TIME_FORMAT,
} from '../../../utils/date';
import JourneyForm from './JourneyForm';
import { JourneyTemplateContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CreateJourneyPage = () => {
  const { createJourney } = useIotsApi();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const mutation = useMutation({ mutationFn: createJourney });
  const { setJourneyNameByDateAndTime } =
    useOutletContext<JourneyTemplateContext>();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  return (
    <JourneyForm
      journey={null}
      isLoading={mutation.isPending}
      onSubmit={values => {
        mutation.mutate(
          {
            operatorId: values.operatorId,
            contractId: values.contractId,
            routeId: null,
            serviceType: values.serviceType,
            departurePortId: values.departurePortId,
            arrivalPortId: values.arrivalPortId,
            day: values.day.format(DAYJS_DATA_DATE_FORMAT),
            time: values.time.format(DAYJS_DATA_TIME_FORMAT),
          },
          {
            onSuccess: newJourneyId => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.journey-creation-success'),
              });
              void disableNavigationGuard(() =>
                navigate(`/admin/journeys/${newJourneyId}`),
              );
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.journey-creation-error'),
                ),
              });
              mutation.reset();
            },
          },
        );
      }}
      onCancel={() => void navigate('/admin/journeys')}
      onDayOrTimeChange={setJourneyNameByDateAndTime}
    />
  );
};

export default CreateJourneyPage;
